import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Search, X, Music2, User, MessageSquare, Loader2 } from "lucide-react";

function MiniAvatar({ src, name }) {
  if (src) return <img src={src} alt={name} className="w-8 h-8 rounded-full object-cover border border-white/10" />;
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  return (
    <div className="w-8 h-8 rounded-full grid place-items-center text-xs font-bold text-white bg-gradient-to-br from-[#FF0050] to-[#8A2BE2]">
      {initial}
    </div>
  );
}

function resolveImg(u) {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  return `${process.env.REACT_APP_BACKEND_URL || ""}${u.startsWith("/") ? u : "/" + u}`;
}

export default function SearchBar() {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState({ artists: [], tracks: [], posts: [] });
  const boxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    function onClick(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!q || q.trim().length < 2) { setRes({ artists: [], tracks: [], posts: [] }); return; }
    setBusy(true);
    const tm = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: q.trim(), limit: 5 } });
        setRes(data);
        setOpen(true);
      } catch { /* silent */ }
      finally { setBusy(false); }
    }, 220);
    return () => clearTimeout(tm);
  }, [q]);

  const total = res.artists.length + res.tracks.length + res.posts.length;
  const labels = t.search || { placeholder: "Rechercher…", artists: "Artistes", tracks: "Morceaux", posts: "Publications", empty: "Aucun résultat" };

  return (
    <div ref={boxRef} className="relative flex-1 max-w-md hidden md:block" data-testid="search-wrap">
      <div className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-3 h-9 transition-colors focus-within:border-[#FF0050]/40">
        {busy ? <Loader2 className="w-4 h-4 text-zinc-400 animate-spin" /> : <Search className="w-4 h-4 text-zinc-400" />}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q.length >= 2 && setOpen(true)}
          placeholder={labels.placeholder}
          className="bg-transparent flex-1 outline-none text-sm text-white placeholder:text-zinc-500"
          data-testid="search-input"
        />
        {q && (
          <button onClick={() => { setQ(""); setOpen(false); }} className="text-zinc-500 hover:text-white" data-testid="search-clear">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div className="absolute left-0 right-0 mt-2 bg-zinc-950/95 border border-white/10 rounded-2xl backdrop-blur-xl shadow-2xl overflow-hidden z-50 anim-in" data-testid="search-dropdown">
          {total === 0 && !busy && (
            <div className="p-5 text-sm text-zinc-500 text-center">{labels.empty}</div>
          )}

          {res.artists.length > 0 && (
            <Section title={labels.artists} icon={User}>
              {res.artists.map((a) => (
                <Link
                  key={a.id}
                  to={`/profile/${a.id}`}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-white/5 transition-colors"
                  data-testid={`search-artist-${a.id}`}
                >
                  <MiniAvatar src={resolveImg(a.avatar)} name={a.name} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white font-semibold truncate">{a.name}</div>
                    {a.bio && <div className="text-[11px] text-zinc-500 truncate">{a.bio}</div>}
                  </div>
                </Link>
              ))}
            </Section>
          )}

          {res.tracks.length > 0 && (
            <Section title={labels.tracks} icon={Music2}>
              {res.tracks.map((tr) => (
                <button
                  key={tr.id}
                  onClick={() => { setOpen(false); navigate("/store"); }}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-white/5 transition-colors text-left"
                  data-testid={`search-track-${tr.id}`}
                >
                  {tr.cover ? (
                    <img src={resolveImg(tr.cover)} alt={tr.title} className="w-9 h-9 rounded-md object-cover" />
                  ) : (
                    <div className="w-9 h-9 rounded-md bg-gradient-to-br from-[#FF0050] to-[#8A2BE2]" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white font-semibold truncate">{tr.title}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{tr.artist_name}</div>
                  </div>
                  <span className="text-xs text-[#00F2FE] font-bold">{tr.is_free ? "FREE" : `${tr.price} ${tr.currency || ""}`}</span>
                </button>
              ))}
            </Section>
          )}

          {res.posts.length > 0 && (
            <Section title={labels.posts} icon={MessageSquare}>
              {res.posts.map((p) => (
                <button
                  key={p.id}
                  onClick={() => { setOpen(false); navigate("/"); }}
                  className="w-full flex items-start gap-3 px-3 py-2 hover:bg-white/5 transition-colors text-left"
                  data-testid={`search-post-${p.id}`}
                >
                  <MiniAvatar src={resolveImg(p.author_avatar)} name={p.author_name} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-zinc-500 truncate">{p.author_name}</div>
                    <div className="text-sm text-white line-clamp-2">{p.content}</div>
                  </div>
                </button>
              ))}
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="border-b border-white/5 last:border-0">
      <div className="flex items-center gap-2 px-3 pt-3 pb-1 text-[10px] uppercase tracking-wider text-zinc-500 font-bold">
        <Icon className="w-3 h-3" /> {title}
      </div>
      <div className="pb-1">{children}</div>
    </div>
  );
}
